import React, { useState, useEffect } from 'react';
import styles from '../../styles/pages/About.module.css';

/**
 * Fixed sidebar navigation for about page positioned in the margin
 * Always stays visible and aligned with content
 * 
 * @param {Object} props - Component props
 * @param {Array} props.links - Array of link objects with id and label
 */
export function AboutSidebar({ links }) {
  const [activeSection, setActiveSection] = useState('bio'); // Set initial state to first section

  // Effect to track scroll position and update active section
  useEffect(() => {
    const sectionElements = links.map(link => 
      document.getElementById(link.id)
    ).filter(Boolean);
    
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200; // Slightly increased offset
      
      // Find the current active section based on scroll position
      const currentSection = sectionElements.find(section => {
        if (!section) return false;
        const sectionTop = section.offsetTop;
        const sectionBottom = sectionTop + section.offsetHeight;
        return scrollPosition >= sectionTop && scrollPosition < sectionBottom;
      });
      
      if (currentSection) {
        setActiveSection(currentSection.id);
      }
    };
    
    // Debounce scroll handler for better performance
    let timeoutId;
    const debouncedHandleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 10);
    };
    
    // Add scroll event listener
    window.addEventListener('scroll', debouncedHandleScroll);
    // Initial check after a short delay to ensure DOM is ready
    setTimeout(handleScroll, 100);
    
    // Clean up
    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll);
      clearTimeout(timeoutId);
    };
  }, [links]);
  
  // Handle smooth scrolling when clicking a link
  const handleLinkClick = (e, targetId) => {
    e.preventDefault();
    const targetElement = document.getElementById(targetId);
    
    if (targetElement) {
      const headerHeight = 144; // Match the CSS top position of sidebar
      const extraOffset = 20; // Additional offset for better positioning
      
      window.scrollTo({
        top: targetElement.offsetTop - headerHeight - extraOffset,
        behavior: 'smooth'
      });
      setActiveSection(targetId);
    }
  };

  return (
    <aside className={styles.resumeSidebar}>
      <div className={styles.sidebarContent}>
        <h3>Table of Contents</h3>
        <nav className={styles.sidebarNav}>
          {links.map((link) => (
            <a 
              key={link.id} 
              href={`#${link.id}`}
              className={`${styles.sidebarLink} ${activeSection === link.id ? styles.sidebarLinkActive : ''}`}
              onClick={(e) => handleLinkClick(e, link.id)}
            >
              {link.label}
            </a>
          ))}
        </nav>
      </div>
    </aside>
  );
}
